import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import {
  getPhoneCountryOptions,
  registerAccount,
  validateEmail,
  validatePersonName,
  validateUsername,
  isStrongPassword,
  parseAndValidatePhone,
} from "@/services/registrationService";
import type { CountryCode } from "libphonenumber-js";
import { buildLoginUrlWithReturnTo, getSafeReturnTo } from "@/lib/publicRoutes";
import { Brain, Mail, CheckCircle2, Eye, EyeOff } from "lucide-react";
import { AuthMarketingPanel } from "@/components/AuthMarketingPanel";

export default function Signup() {
  const returnTo = getSafeReturnTo(new URLSearchParams(window.location.search).get("returnTo"));
  const loginUrl = buildLoginUrlWithReturnTo(returnTo);
  const [email, setEmail] = useState("");
  const [username, setUsername] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [countryIso, setCountryIso] = useState<CountryCode>("US");
  const [phoneNational, setPhoneNational] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [couponCode, setCouponCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [isVerificationSent, setIsVerificationSent] = useState(false);
  const [verificationEmail, setVerificationEmail] = useState("");
  const { toast } = useToast();
  const [, setLocation] = useLocation();

  const countryOptions = useMemo(() => getPhoneCountryOptions(), []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    const emailErr = validateEmail(email);
    if (emailErr) {
      setError(emailErr);
      return;
    }
    const userErr = validateUsername(username);
    if (userErr) {
      setError(userErr);
      return;
    }
    const fnErr = validatePersonName(firstName, "First name");
    if (fnErr) {
      setError(fnErr);
      return;
    }
    const lnErr = validatePersonName(lastName, "Last name");
    if (lnErr) {
      setError(lnErr);
      return;
    }
    const phoneErr = parseAndValidatePhone(countryIso, phoneNational);
    if ("error" in phoneErr) {
      setError(phoneErr.error);
      return;
    }
    if (!isStrongPassword(password)) {
      setError(
        "Password must be at least 8 characters and include uppercase, lowercase, a number, and a special character"
      );
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setIsLoading(true);
    try {
      const result = await registerAccount({
        email: email.trim(),
        password,
        username: username.trim().toLowerCase(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        countryIso,
        phoneNational,
        couponCode: couponCode.trim() || undefined,
      });

      if (!result.ok) {
        setError(result.error);
        return;
      }

      if (result.ghlWarning) {
        toast({
          title: "Contact sync",
          description: result.ghlWarning,
        });
      }

      if (!result.emailVerificationRequired) {
        await queryClient.invalidateQueries({ queryKey: ["auth-user"] });
        toast({
          title: "Welcome!",
          description: result.ghlSynced
            ? "Your account is ready and your profile was saved."
            : "Your account is ready.",
        });
        setLocation(returnTo);
        return;
      }

      setVerificationEmail(result.email);
      setIsVerificationSent(true);
      toast({
        title: "Account created",
        description: "Please check your email to verify your account.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isVerificationSent) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="space-y-4 text-center">
            <div className="flex justify-center">
              <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
                <Mail className="h-8 w-8 text-primary" />
              </div>
            </div>
            <div>
              <CardTitle className="text-2xl">Check your email</CardTitle>
              <CardDescription>
                We've sent a verification email to <strong>{verificationEmail}</strong>
              </CardDescription>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <div className="flex items-start gap-3">
                <CheckCircle2 className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                <div className="space-y-1 text-sm">
                  <p className="font-medium">Verification email sent</p>
                  <p className="text-muted-foreground">
                    Please check your inbox and click the verification link to activate your account.
                  </p>
                </div>
              </div>
            </div>
            <div className="space-y-2 text-sm text-muted-foreground">
              <p>After verifying your email:</p>
              <ol className="list-decimal list-inside space-y-1 ml-2">
                <li>Return to the login page</li>
                <li>Sign in with your credentials</li>
              </ol>
            </div>
            <Button
              className="w-full"
              onClick={() => setLocation(loginUrl)}
              data-testid="button-go-to-login"
            >
              Go to Login Page
            </Button>
            <div className="text-center text-sm">
              <span className="text-muted-foreground">Didn't receive the email? </span>
              <button
                type="button"
                className="text-primary hover:underline"
                onClick={() => setIsVerificationSent(false)}
                data-testid="link-back-to-signup"
              >
                Try again
              </button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="min-h-screen flex flex-col md:flex-row">
        <div className="md:w-1/2 bg-muted/30 border-b md:border-b-0 md:border-r">
          <AuthMarketingPanel />
        </div>
        <div className="md:w-1/2 flex items-center justify-center p-4">
      <Card className="w-full max-w-lg">
        <CardHeader className="space-y-4 text-center">
          <div className="flex justify-center">
            <div className="h-12 w-12 rounded-lg bg-primary flex items-center justify-center">
              <Brain className="h-6 w-6 text-primary-foreground" />
            </div>
          </div>
          <div>
            <CardTitle className="text-2xl">Join the waitlist</CardTitle>
            <CardDescription>
              Create your account — we&apos;ll open access when we launch
            </CardDescription>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName">First name</Label>
                <Input
                  id="firstName"
                  type="text"
                  autoComplete="given-name"
                  placeholder="Jane"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  required
                  data-testid="input-first-name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName">Last name</Label>
                <Input
                  id="lastName"
                  type="text"
                  autoComplete="family-name"
                  placeholder="Doe"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  required
                  data-testid="input-last-name"
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="username">Username</Label>
              <Input
                id="username"
                type="text"
                autoComplete="username"
                placeholder="jane_doe"
                value={username}
                onChange={(e) => setUsername(e.target.value.toLowerCase())}
                required
                minLength={3}
                data-testid="input-username"
              />
              <p className="text-xs text-muted-foreground">
                3–24 characters, starts with a letter; lowercase letters, numbers, underscores only. Must be unique.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="input-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="country">Country (for phone)</Label>
              <Select value={countryIso} onValueChange={(v) => setCountryIso(v as CountryCode)}>
                <SelectTrigger id="country" data-testid="select-country">
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent className="max-h-[280px]">
                  {countryOptions.map((c) => (
                    <SelectItem key={c.iso} value={c.iso}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="phone">Mobile number</Label>
              <Input
                id="phone"
                type="tel"
                autoComplete="tel-national"
                placeholder="Local number without country code"
                value={phoneNational}
                onChange={(e) => setPhoneNational(e.target.value)}
                required
                data-testid="input-phone"
              />
              <p className="text-xs text-muted-foreground">
                Enter your national number; we validate it for the country you selected.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Create a strong password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="input-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  data-testid="button-toggle-password"
                >
                  {showPassword ? <EyeOff className="h-4 w-4 shrink-0" /> : <Eye className="h-4 w-4 shrink-0" />}
                </button>
              </div>
              <p className="text-xs text-muted-foreground">
                At least 8 characters with uppercase, lowercase, a number, and a special character.
              </p>
            </div>
            <div className="space-y-2">
              <Label htmlFor="confirmPassword">Confirm password</Label>
              <div className="flex w-full items-stretch rounded-md border border-input bg-background ring-offset-background focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2">
                <Input
                  id="confirmPassword"
                  type={showConfirmPassword ? "text" : "password"}
                  autoComplete="new-password"
                  placeholder="Confirm your password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  className="h-9 min-w-0 flex-1 border-0 bg-transparent shadow-none focus-visible:ring-0 focus-visible:ring-offset-0"
                  data-testid="input-confirm-password"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-r-md text-muted-foreground hover:bg-muted hover:text-foreground sm:w-10"
                  aria-label={showConfirmPassword ? "Hide confirm password" : "Show confirm password"}
                  data-testid="button-toggle-confirm-password"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4 shrink-0" /> : <Eye className="h-4 w-4 shrink-0" />}
                </button>
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="coupon">Coupon code (optional)</Label>
              <Input
                id="coupon"
                type="text"
                placeholder="e.g. WELCOME1500"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value)}
                autoComplete="off"
                data-testid="input-coupon"
              />
              <p className="text-xs text-muted-foreground">
                New accounts include 1,000 free credits. Each coupon code works once per account; valid codes add bonus credits at first sign-in.
              </p>
            </div>
            {error && (
              <p className="text-sm text-destructive" data-testid="text-error">
                {error}
              </p>
            )}
            <Button
              type="submit"
              className="w-full"
              disabled={isLoading}
              data-testid="button-signup"
            >
              {isLoading ? "Creating account..." : "Join waitlist"}
            </Button>
          </form>
          <div className="mt-4 text-center text-sm">
            <span className="text-muted-foreground">Already have an account? </span>
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => setLocation(loginUrl)}
              data-testid="link-login"
            >
              Sign in
            </button>
          </div>
        </CardContent>
      </Card>
        </div>
      </div>
    </div>
  );
}
